import type { TemplateDifficulty } from './types';

// Where the disabled template tile sends creators who aren't on the paid
// templates programme.
export const TEMPLATE_INTEREST_URL =
  'https://lureo.typeform.com/to/jcQSAU0N?typeform-source=www.lureo.com';

export const TEMPLATE_DIFFICULTIES: { value: TemplateDifficulty; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

// The only source of gallery categories. Stored on scene_origins.scene_origin_category
// as the display string the gallery card renders. Derived from the anchor, never
// chosen: a creator picking a category their scene contradicts would break gallery
// filtering, and there is no second vocabulary to keep in step.
export const ANCHOR_CATEGORY_TO_TEMPLATE_CATEGORY: Record<string, string> = {
  place_anywhere: 'Place Anywhere',
  image_anchor: 'Image Anchor',
  immersal: 'Scan Anchor',
  geospatial_anchor: 'Map Anchor',
};

// Kept in step with the backend's TemplateFeatureCatalog, which labels these keys on the
// Review Desk. Stored on scene_origins.scene_origin_features as the keys, never the labels.
export interface TemplateFeature {
  key: string;
  label: string;
  group: string;
}

export const TEMPLATE_FEATURE_GROUPS = [
  'Anchoring',
  'Animation & timing',
  'Look & feel',
  'Media',
] as const;

export const TEMPLATE_FEATURES: TemplateFeature[] = [
  { key: 'image_anchor', label: 'Image anchor', group: 'Anchoring' },
  { key: 'scan_anchor', label: 'Scan anchor', group: 'Anchoring' },
  { key: 'map_anchor', label: 'Map anchor', group: 'Anchoring' },
  { key: 'place_anywhere', label: 'Place anywhere', group: 'Anchoring' },

  { key: 'keyframe_animation', label: 'Keyframe animation', group: 'Animation & timing' },
  { key: 'character_animation', label: 'Character animation', group: 'Animation & timing' },
  { key: 'clip_sequencing', label: 'Clip sequencing', group: 'Animation & timing' },

  { key: 'masks', label: 'Masks and portals', group: 'Look & feel' },
  { key: 'materials', label: 'Materials', group: 'Look & feel' },
  { key: 'open_brush', label: 'Open Brush artwork', group: 'Look & feel' },

  { key: 'audio', label: 'Audio', group: 'Media' },
  { key: 'video', label: 'Video', group: 'Media' },
  { key: 'screen_messages', label: 'Screen messages', group: 'Media' },
];

export const MAX_TEMPLATE_TAGS = 5;
export const MIN_TEMPLATE_DESCRIPTION_LENGTH = 300;
export const MIN_TEMPLATE_LEARNINGS_LENGTH = 100;

const FEATURE_LABELS = new Map(TEMPLATE_FEATURES.map((f) => [f.key, f.label]));

// A key this build doesn't know still renders as itself rather than vanishing from a
// gallery card or a summary.
export function templateFeatureLabel(key: string): string {
  return FEATURE_LABELS.get(key) ?? key;
}

export function isTemplateDifficulty(value: string): value is TemplateDifficulty {
  return value === 'beginner' || value === 'intermediate' || value === 'advanced';
}
